import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw, { TwStyle } from 'twin.macro';

import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { Loader } from '@components//Loader';
import { CloseIcon } from '@icons/CloseIcon';
import { Modal, ModalContent, ModalHeader } from '@components/Modal';
import { IAvatar, useAvatar } from '@app/hooks/useAvatar';
import { useUser } from '@auth_module/hooks/useUser';

const ModalTitle = tw.span`text-sm text-text-and-icon text-left font-semibold`;
const UserEmail = tw.span`block mx-auto text-center text-xs`;
const AvatarImage = tw.img`cursor-pointer rounded-full box-border w-14 h-14`;
const CurrentAvatar = tw.img`mx-auto w-32 h-32`;
const AvatarsContainer = tw.div`grid grid-cols-[repeat(5,1fr)] gap-4`;
const Seperator = tw.div`w-full h-[1px] bg-muted`;
const CloseButton = tw.button`flex items-center justify-center`;
const SubmitButton = tw(
  Button.Primary
)`block !w-full disabled:opacity-70 disabled:cursor-not-allowed`;
const buttonLoader = tw`!border-[#ffffff] border-b-transparent!` as TwStyle;

export const EditAvatarModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { userAvatar, avatars, isLoadingAvatars, isUpdateLoading, updateAvatar, isUpdateSuccess } =
    useAvatar();
  const [selectedAvatar, setSelectedAvatar] = useState<IAvatar | null>(null);

  const selectedAvatarUrl = useMemo(
    () => (selectedAvatar ? selectedAvatar.url : userAvatar.url),
    [userAvatar, selectedAvatar]
  );

  const handleAvatarUpdate = () => selectedAvatar?.id && updateAvatar(selectedAvatar.id);

  useEffect(() => {
    if (isUpdateLoading) return;

    isUpdateSuccess && onClose();
  }, [isUpdateLoading, isUpdateSuccess]);

  return (
    <Modal onClose={onClose} closeOnClickOutside={true}>
      <ModalHeader className='max-w-[500px] mx-auto'>
        <ModalTitle>{t('avatar.modal_title')}</ModalTitle>
        <CloseButton onClick={onClose}>
          <CloseIcon tw='fill-text-and-icon' />
        </CloseButton>
      </ModalHeader>
      <ModalContent tw='bg-neutral-background text-text-and-icon p-6 rounded-t-none max-w-[500px]'>
        <Spacer size='lg' direction='vertical' />
        <CurrentAvatar src={selectedAvatarUrl} alt='User avatar' />
        <Spacer size='sm' direction='vertical' />
        <UserEmail>{user?.email}</UserEmail>
        <Spacer size='lg' direction='vertical' />
        <Seperator />
        <Spacer size='lg' direction='vertical' />
        {isLoadingAvatars ? (
          <Loader size='lg' theme='light' css={buttonLoader} />
        ) : (
          <AvatarsContainer>
            {avatars?.map((avatar) => (
              <AvatarImage
                key={avatar?.id}
                src={avatar?.url}
                title={avatar?.name}
                onClick={() => setSelectedAvatar(avatar)}
                alt={`Sounder avatar - ${avatar?.name}`}
              />
            ))}
          </AvatarsContainer>
        )}
        <Spacer size='lg' direction='vertical' />
        <Seperator />
        <Spacer size='xl' direction='vertical' />
        <SubmitButton onClick={handleAvatarUpdate} disabled={isUpdateLoading} role='button'>
          {isUpdateLoading ? (
            <Loader size='sm' theme='light' css={buttonLoader} />
          ) : (
            t('avatar.modal_button')
          )}
        </SubmitButton>
      </ModalContent>
    </Modal>
  );
};
